export type ConnectInitOptions = {
	workspaceId: string;
	definitionId: string;
	layout?: {
		verticalOffset?: number;
	};
};

declare global {
	interface Window {
		Connect?: {
			init?: (options: ConnectInitOptions) => unknown;
			[key: string]: unknown;
		};
	}
}

const SCRIPT_ID = 'connect-host-script';
const BODY_CLASS = 'connect-enabled';

// The host script calls this when it is ready.
const ON_READY_KEY = '__onConnectHostReady__';

let scriptPromise: Promise<void> | null = null;
let hostReadyPromise: Promise<void> | null = null;
let initDone = false;

function loadScriptOnce(src: string): Promise<void> {
	const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
	if (existing) return Promise.resolve();

	return new Promise<void>((resolve, reject) => {
		const s = document.createElement('script');
		s.id = SCRIPT_ID;
		s.async = true;
		s.src = src;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error('Failed to load Connect script'));
		document.head.appendChild(s);
	});
}

async function getHostScriptUrl(workspaceId: string, definitionId: string): Promise<string> {
	const loaderUrl =
		'https://webassist.onconverse.app/api/v1/loader/workspaces/' +
		encodeURIComponent(workspaceId) +
		'/definitions/' +
		encodeURIComponent(definitionId);

	const res = await fetch(loaderUrl);
	const data = (await res.json()) as { url?: string };

	if (!data.url) throw new Error('Connect loader did not return url');
	return data.url;
}

function ensureHostReadyHook(): Promise<void> {
	if (hostReadyPromise) return hostReadyPromise;

	hostReadyPromise = new Promise<void>((resolve) => {
		// Use an 'unknown' bridge to satisfy TS for dynamic window keys.
		const w = window as unknown as Record<string, unknown>;

		w[ON_READY_KEY] = (host: unknown) => {
			delete w[ON_READY_KEY];

			// The host object becomes window.Connect.
			window.Connect = host as Window['Connect'];

			resolve();
		};
	});

	return hostReadyPromise;
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const t = window.setTimeout(() => reject(new Error(message)), ms);
		p.then((v) => {
			window.clearTimeout(t);
			resolve(v);
		}).catch((e) => {
			window.clearTimeout(t);
			reject(e);
		});
	});
}

export async function enableConnect(options: ConnectInitOptions): Promise<void> {
	document.body.classList.add(BODY_CLASS);

	// Install the global ready hook BEFORE loading the host script.
	const ready = ensureHostReadyHook();

	if (!scriptPromise) {
		scriptPromise = (async () => {
			const url = await getHostScriptUrl(options.workspaceId, options.definitionId);
			await loadScriptOnce(url);
		})();
	}

	// Wait for script load + the host ready callback.
	await scriptPromise;
	await withTimeout(ready, 10000, 'Connect host did not call __onConnectHostReady__');

	if (!initDone) {
		const initFn = window.Connect?.init;
		if (typeof initFn !== 'function') {
			throw new Error('window.Connect.init not available');
		}

		initFn(options);
		initDone = true;
	}
}

export function disableConnect(): void {
	// Do not destroy/unmount here. Hide via CSS class instead.
	document.body.classList.remove(BODY_CLASS);
}
